import type { ReactNode } from 'react';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@shared/components/ui/tooltip';
import { APP } from '@shared/constants/app';
import { QueryProvider } from './QueryProvider';
import { ThemeProvider } from './ThemeProvider';
import { AuthProvider } from './AuthProvider';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Composes every cross-cutting provider in the correct order:
 * Theme (visual) → Query (data) → Auth (session bootstrap, depends on Query) →
 * Tooltip (Radix context). Global toast portal is mounted once here.
 *
 * `TooltipProvider` belongs at the root because it is CONTEXT, not layout:
 * every `Tooltip` needs an ancestor provider or Radix throws
 * "Tooltip must be used within TooltipProvider" while rendering. Until now each
 * screen supplied its own, so the guarantee was per-component and one screen
 * that forgot it crashed — which is exactly what happened on the Bank &
 * Statutory step. One provider here makes the context an invariant of the app
 * rather than something every new screen has to remember.
 *
 * `delayDuration` matches the value the existing per-screen providers use, so
 * nothing changes for tooltips that already had one. Those local providers are
 * left in place: Radix permits nesting, an inner provider simply wins for its
 * subtree, and `SimpleTooltip` sets `delayDuration` on the tooltip itself
 * anyway — so removing them would be churn, not a fix.
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <ThemeProvider>
      <QueryProvider>
        <AuthProvider>
          <TooltipProvider delayDuration={200}>
            {children}
            <Toaster richColors closeButton position="top-right" duration={APP.TOAST_DURATION_MS} />
          </TooltipProvider>
        </AuthProvider>
      </QueryProvider>
    </ThemeProvider>
  );
}
